<template>
  <div class="post-close-connect-v2">
    <div class="view-title-container">
      <div class="view-title-icon">
        <i class="fa-solid fa-file-invoice-dollar"></i>
      </div>
      <div>
        <h2 class="view-title">Post Close Connect</h2>
      </div>
    </div>

    <div v-if="loading" class="post-close-connect-v2__loader-container">
      <k-loader size="large" type="infinite-spinner"></k-loader>
    </div>

    <!--Error Dialog-->
    <error-dialog v-if="showErrorDialog" :errorMsg="errorMsg" :errorTitle="errorTitle" @close="onCloseErrorDialog" />

    <!--Summary Dialog-->
    <k-dialog v-if="isSummaryDialogVisible" :title="summaryDialogTitle" class="default__dialog post-close-summary-dialog" @close="onCloseSummaryDialog">
      <CommandCenterTable
        :show-header="false"
        :columns="summaryCardColumns"
        :data="summaryCardGridItems"
        :default-page-size="50"
        :filterable="true">
      </CommandCenterTable>
    </k-dialog>

    <div v-if="!loading" class="pipeline-container">
      <div class="post-close-summary-cards-cont">
        <summary-card :postCloseSummaryData="postCloseSummaryData" :summaryCardTitles="summaryCardTitles" @open-summary-dialog="onOpenSummaryDialog" />
      </div>

      <div class="cc-apps-toolbar">
        <div class="cc-apps-toolbar-left">
          <div class="cc-apps-search-wrap">
            <i class="fa-duotone fa-magnifying-glass"></i>
            <input class="cc-apps-search" type="text" placeholder="Search in all columns..."
              :value="searchWord" @input="onSearchChange({ value: $event.target.value })" />
          </div>
        </div>

        <div class="cc-apps-toolbar-right">
          <button class="cc-apps-refresh-btn" @click="onRefresh">
            <i class="fa-regular fa-arrow-rotate-right"></i> Refresh
          </button>
        </div>
      </div>

      <CommandCenterTable
        :show-header="false"
        :columns="columns"
        :data="filteredItems"
        :default-page-size="50"
        :filterable="true"
        :active-sort="sort"
        @sort="onSortChange"
        @row-click="onRowClick">
      </CommandCenterTable>
    </div>
  </div>
</template>

<script>
  import { Loader } from "@progress/kendo-vue-indicators";
  import { Dialog } from '@progress/kendo-vue-dialogs';
  import cloneDeep from 'lodash.clonedeep';
  import apiService from '../api/api.service.js';
  import auth from '../authConfig.js';
  import { debounce } from 'lodash';

  //Components
  import CommandCenterTable from './commandCenter/CommandCenterTable.vue';
  import SummaryCard from './summaryCard.vue';
  import ErrorDialog from './errorDialog.vue';

  export default {
    components: {
      'k-loader': Loader,
      'k-dialog': Dialog,
      CommandCenterTable,
      'summary-card': SummaryCard,
      'error-dialog': ErrorDialog,
    },
    data() {
      return {
        columns: [
           { field: 'daystocust18fv', title: 'Days on Line', type: 'number' },
          { field: 'ms_fun', title: 'Funded Date', type: 'date' },
          { field: 'loannumber', title: 'Loan #' },
          { field: 'borrowerfullname', title: 'Borrower' },
          { field: 'f2', title: 'Loan Amount', type: 'currency', decimals: 2 },
          { field: 'losinvestor', title: 'Investor' },
                 { field: 'lienposition', title: 'Lien' },
          { field: 'cust19fv', title: 'Investor #' },
          { field: 'postcloser', title: 'Post Closer' },
          { field: 'postclosestatus', title: 'Status' },
          { field: 'warehouseline', title: 'Warhouse Line' },
          { field: 'orgid', title: 'Org' },
        ],
        azureId: null,
        errorMsg: '',
        errorTitle: '',
        filteredItems: [],
        firstRender: true,
        fetchInterval: null,
        isSummaryDialogVisible: false,
        loading: true,
        postCloseSummaryData: null,
        postCloseGridData: [],
        searchWord: "",
        showErrorDialog: false,
        sort: [],
        summaryCardColumns: [
          { field: 'name', title: 'Name' },
          { field: 'units', title: 'Units', type: 'number' },
          { field: 'volume', title: 'Volume', type: 'currency', decimals: 2 },
        ],
        summaryCardGridItems: [],
        summaryCardTitles: [
          { title: "Units", value: 0 },
          { title: "Volume", value: 0 },
          { title: "Aged Loans > 20 days", value: 0 },
          { title: "Investor Summary", value: [] },
          { title: "Post Closer Summary", value: [] },
          { title: "Date of Oldest Request", value: 0 },
          { title: "Total Requests", value: 0 },
          { title: "Request > 20 days", value: 0 }
        ],
        summaryDialogTitle: "",
        userRoles: [],
      };
    },
    async mounted() {
      window.addEventListener("resize", debounce(this.getScreenWidth, 100));

      this.userRoles = this.getRolesFromStorage('user_roles');

      if (this.userRoles.includes("Post Closer") || this.userRoles.includes("Corp")) {
        this.fetchData();
        this.firstRender = false;
      } else {
        this.loading = false;
      }

      this.fetchInterval = setInterval(() => {
        if (this.userRoles.includes("Post Closer") || this.userRoles.includes("Corp")) {
          this.fetchData();
        } else {
          this.loading = false;
        }
      }, 300000);
    },
    beforeDestroy() {
      window.removeEventListener("resize", this.getScreenWidth);
      clearInterval(this.fetchInterval);
    },
    methods: {
      getRolesFromStorage(key) {
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            return parsed.map(x => String(x || '').trim()).filter(Boolean);
          }
          return String(parsed || '').split(',').map(x => x.trim()).filter(Boolean);
        } catch {
          return raw.split(',').map(x => x.trim()).filter(Boolean);
        }
      },
      async fetchData() {
        if (this.firstRender) {
          this.loading = true;
        }
        try {
          const tokenResponse = await auth.acquireTokenResponse();
          this.azureId = localStorage.getItem('azure_id');
          if (!tokenResponse) throw new Error("Failed to acquire token response");

          const response = await apiService.GetPostCloseSummarysV2(tokenResponse.accessToken);

          const data = response.data;
          if (data && data.issuccess) {
            this.postCloseGridData = data.data.postclosedatalist.map(item => {
              item.postclosestatus = "Pending Upload";

              if (item.cxpcseandoclear && (item.cxpcseandoclear == "X" || item.cxpcseandoclear == "x")) {
                item.postclosestatus = "E&O's Cleared";
                return item;
              }

              if (item.cxpcseandoreceive && (item.cxpcseandoreceive == "X" || item.cxpcseandoreceive == "x")) {
                item.postclosestatus = "E&O's Received";
                return item;
              }

              if (item.cxpcspackageupload && (item.cxpcspackageupload == "X" || item.cxpcspackageupload == "x")) {
                item.postclosestatus = "Package Uploaded";
                return item;
              }

              return item;
            });

            this.summaryCardTitles = [
              { title: "Units", value: data.data.postclosesummary.units ? data.data.postclosesummary.units : "0" },
              { title: "Volume", value: data.data.postclosesummary.volume ? data.data.postclosesummary.volume : "0" },
              { title: "Aged Loans > 20 days", value: data.data.postclosesummary.agedloansover20days ? data.data.postclosesummary.agedloansover20days : "0" },
              { title: "Investor Summary", value: data.data.postclosesummary.investorsummary ? data.data.postclosesummary.investorsummary : [] },
              { title: "Post Closer Summary", value: data.data.postclosesummary.postclosesummary ? data.data.postclosesummary.postclosesummary : [] },
              {
                title: "Date of Oldest Request",
                value: data.data.postclosesummary.dateofoldestrequest
                  ? new Date(data.data.postclosesummary.dateofoldestrequest).toLocaleDateString("en-US", {
                      month: "2-digit", day: "2-digit", year: "numeric"
                    })
                  : "N/A"
              },
              { title: "Total Requests", value: data.data.postclosesummary.totalrequests ? data.data.postclosesummary.totalrequests : "0" },
              { title: "Request > 20 days", value: data.data.postclosesummary.requestover20days ? data.data.postclosesummary.requestover20days : "0" }
            ];

            this.processData();
          } else {
            console.error("No valid data received from API");
            this.onRequestError('Post Close Error', "No valid data received from API");
            this.postCloseGridData = [];
          }
        } catch (error) {
          this.onRequestError('Post Close Error', error.message);
        } finally {
          this.loading = false;
          this.processData();
        }
      },
      getFilteredItems() {
        if (this.searchWord) {
          return this.postCloseGridData.filter(item => {
            return Object.values(item).some(val => val && val.toString().toLowerCase().includes(this.searchWord.toLowerCase()));
          });
        }
        return cloneDeep(this.postCloseGridData);
      },
      getScreenWidth() {
        this.windowWidth = window.innerWidth;
      },
      onCloseErrorDialog() {
        this.showErrorDialog = false;
        if (!this.showErrorDialog) {
          this.errorMsg = '';
          this.errorTitle = '';
        }
      },
      onCloseSummaryDialog() {
        this.isSummaryDialogVisible = false;
      },
      onOpenSummaryDialog(title) {
        this.isSummaryDialogVisible = true;
        this.summaryDialogTitle = title;

        const selectedSummary = this.summaryCardTitles.find(item => item.title === title);

        if (selectedSummary && selectedSummary.value) {
          this.summaryCardGridItems = selectedSummary.value.map(item => {
            item.volume = item.volume ? parseFloat(item.volume) : 0;
            return item;
          });
        } else {
          console.error(`No summary found for title: ${title}`);
          this.summaryCardGridItems = [];
        }
      },
      onRefresh() {
        this.fetchData();
      },
      onRequestError(errorTitle, errorMsg) {
        this.errorTitle = errorTitle;
        this.errorMsg = errorMsg || 'Please try again';
        this.showErrorDialog = true;
      },
      onRowClick(e) {
        const dataItem = e.dataItem || e;
        let environment = window.location.href;
        let baseUrl;

        if (environment.includes("localhost:")) {
          baseUrl = "http://localhost:8081/PostClose";
        } else if (environment.includes("https://dev.swiftapp.ai/")) {
          baseUrl = "https://dev-swift.goldstarfinancial.com/PostClose";
        } else {
          baseUrl = "https://swift.goldstarfinancial.com/PostClose";
        }

        window.open(`${baseUrl}?id=${dataItem.id}`, '_blank');
      },
      onSearchChange(evt) {
        this.searchWord = evt.value;
        this.processData();
      },
      onSortChange({ field, dir }) {
        this.sort = [{ field, dir }];
        this.processData();
      },
      processData() {
        this.filteredItems = this.getFilteredItems();
      },
    },
  };
</script>

<style>
  @import '/src/assets/css/postCloseConnect.css';
  @import '/src/assets/css/postCloseConnectV2.css';
</style>
